import { ref } from 'vue'

/**
 * Whether this hotel sells rooms on the web at all.
 *
 * This site is the face of the `distribution` module, and from this release the API
 * refuses every route of a module the hotel has not bought with 403 and
 * `{"error": {"code": 19}}` (`pms-api/HTTP_API/Booking.go` gates the whole /booking
 * tree; docs/MODULI.md §3.2). The gate is the first middleware there, so an unlicensed
 * house is refused before anything else runs — including the very first call this site
 * makes.
 *
 * It is kept apart from the store's `closed` on purpose, though a visitor is told
 * something similar by both. `closed` is "the network answered nothing useful" — a
 * deployment with no house on sale, an API that is down, a season that ended — and it
 * is a guess made from an empty answer. This is the API saying exactly what is wrong,
 * and it is the case a hotelier ringing about "our booking page is broken" needs
 * distinguished, because the fix is a licence and not a bug.
 *
 * A module-level ref rather than a store field: `stores/fetch.ts` is what sees the
 * refusal, and a fetch store that reached back into the booking store which owns it
 * would be a cycle for one boolean.
 *
 * The notice carries no retry and nothing clears this on a timer: a licence is changed
 * in the vendor console, not by a visitor pressing a button.
 *
 * What does clear it is the house changing under the site — `loadProperty` resolving a
 * different one from the one the refusal was recorded about. Today that seam can only
 * fire on a fresh load, where this ref (which lives no longer than the page) is clear
 * anyway: the site sells ONE house, taken as the first of the deployment's list, and
 * there is no list screen a visitor could go back to. When a second house appears —
 * the house on the hostname or in the path, as `stores/booking.ts` says — this is
 * already the place that forgets the other house's answer, rather than a site that
 * shows Hotel A's locked door while selling Hotel B.
 */
export const moduleUnlicensed = ref(false)

/**
 * Forgets a refusal, because it was recorded about a different house.
 *
 * Called when the house the site sells changes — never on a timer and never from the
 * notice itself, which would be the retry loop this flag exists to avoid.
 */
export const clearModuleRefusal = () => {
    moduleUnlicensed.value = false
}

/** The API's code for a module this hotel has not bought. */
const MODULE_NOT_LICENSED = 19

/**
 * Reads one refused answer and says whether it was this refusal.
 *
 * Both shapes of body are taken: the parsed object a `.json()` call hands to
 * `onFetchError`, and the raw text of a call that never asked for JSON.
 */
export const noteModuleRefusal = (status: number | undefined, body: unknown): boolean => {
    if (status !== 403) return false

    let parsed = body

    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed)
        } catch {
            // A 403 whose body is not JSON is an ordinary refusal, not this one.
            return false
        }
    }

    if ((parsed as { error?: { code?: number } })?.error?.code !== MODULE_NOT_LICENSED) return false

    moduleUnlicensed.value = true

    return true
}
